/**
 * Decisions for the magic-link consume screen (AUTH-R30). Pure module so the
 * screen states can be unit-tested without rendering.
 *
 * - A refused link (401, `magic_link_invalid`: expired, already used or
 *   unknown) shows "This sign-in link has expired or was already used" with
 *   one way forward; a network/server error shows "couldn't check the link"
 *   with Try again. Never a blank or stuck screen.
 * - Someone already signed in on this browser (e.g. the link opened twice) is
 *   routed into the app instead of seeing an error.
 * - The email a sign-in link or code was last requested for, on this browser,
 *   is remembered so the screen can send a fresh code to it in one tap.
 */
import type { AuthPhase } from '../../context/AuthContext';
import { isPublicPhase, pathForPhase } from '../../navigation/phaseRedirect';
import { ROUTES } from '../../navigation/routes';

export const MAGIC_LINK_INVALID_CODE = 'magic_link_invalid';

export type ConsumeStatus = 'verifying' | 'success' | 'invalid' | 'error';

type ErrorShape = {
  status?: unknown;
  response?: { status?: unknown; data?: { detail?: unknown } };
  detail?: unknown;
} | null | undefined;

/** `invalid`: the server refused the link. `error`: we could not reach a verdict (network, 5xx, 429). */
export function classifyConsumeFailure(error: unknown): 'invalid' | 'error' {
  const e = error as ErrorShape;
  const status = e?.response?.status ?? e?.status;
  const detail = e?.response?.data?.detail ?? e?.detail;
  if (detail && typeof detail === 'object' && (detail as { code?: unknown }).code === MAGIC_LINK_INVALID_CODE) {
    return 'invalid';
  }
  if (status === 400 || status === 401 || status === 404 || status === 422) return 'invalid';
  return 'error';
}

/**
 * Where to send the user while the screen shows `status`: the app when this
 * browser is already signed in (and the link itself did not work), else stay.
 */
export function consumeRedirect(status: ConsumeStatus, phase: AuthPhase): string | null {
  if (phase === 'loading') return null;
  if (status === 'success') return pathForPhase(phase);
  if ((status === 'invalid' || status === 'error') && !isPublicPhase(phase)) return pathForPhase(phase);
  return null;
}

/**
 * True when the user actually sees the "expired or already used" / "couldn't
 * check the link" screen: a failed status that is not redirected into the app
 * because this browser is already signed in. Drives
 * `app_magic_link_invalid_screen_shown`.
 */
export function failureScreenShown(status: ConsumeStatus, phase: AuthPhase): boolean {
  if (phase === 'loading') return false;
  if (status !== 'invalid' && status !== 'error') return false;
  return consumeRedirect(status, phase) === null;
}

/** Login route that opens on the code step for `email` (PasswordlessLoginScreen reads these params). */
export function freshCodeLoginPath(email: string, codeSent: boolean): string {
  const params = `email=${encodeURIComponent(email)}${codeSent ? '&otp_sent=1' : ''}`;
  return `${ROUTES.PUBLIC_LOGIN}?${params}`;
}

const SIGN_IN_EMAIL_KEY = 'mcai_sign_in_email';
const SIGN_IN_EMAIL_TTL_MS = 14 * 24 * 60 * 60 * 1000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function storage(): StorageLike | null {
  try {
    const candidate = (globalThis as { localStorage?: StorageLike }).localStorage;
    return candidate ?? null;
  } catch {
    return null;
  }
}

/** Remember the email a link/code was requested for (this browser only). */
export function rememberSignInEmail(email: string, now: number = Date.now()): void {
  const store = storage();
  if (!store || !email) return;
  try {
    store.setItem(SIGN_IN_EMAIL_KEY, JSON.stringify({ email: email.trim().toLowerCase(), at: now }));
  } catch {
    /* storage full or blocked: the screen falls back to the sign-in form */
  }
}

/** The remembered email, or null when none, expired or unreadable. */
export function rememberedSignInEmail(now: number = Date.now()): string | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(SIGN_IN_EMAIL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { email?: unknown; at?: unknown };
    if (typeof parsed.email !== 'string' || !parsed.email.includes('@')) return null;
    if (typeof parsed.at !== 'number' || now - parsed.at > SIGN_IN_EMAIL_TTL_MS) {
      store.removeItem(SIGN_IN_EMAIL_KEY);
      return null;
    }
    return parsed.email;
  } catch {
    return null;
  }
}

/** Forget it once the user is signed in. */
export function forgetSignInEmail(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(SIGN_IN_EMAIL_KEY);
  } catch {
    /* ignore */
  }
}
